import type React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { type ZodType } from 'zod'
import { useValidationSchema } from './ValidationSchemaContext'

export interface WithFormValidation {
  name: string
}

export const withFormValidation = <P extends object>(WrappedComponent: React.ComponentType<P>): React.ComponentType<P & WithFormValidation> => {
  const ComponentWithFormValidation = (props: P & WithFormValidation) => {
    const methods = useFormContext()
    const validationSchema = useValidationSchema()
    const isRequired = validationSchema ? isFieldRequired(props.name, validationSchema) : false
    const { errors } = methods.formState

    const mapErrorMessage = () => {
      const keys = props.name.split('.')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return keys.reduce((value, key) => value?.[key], errors as any)?.message
    }

    return (
      <Controller
        name={props.name}
        control={methods.control}
        render={({ field }) => <WrappedComponent {...(props as P)} {...field} error={mapErrorMessage()} required={(props as Record<string, unknown>).required === true || isRequired} />}
      />
    )
  }
  ComponentWithFormValidation.displayName = `WithFormValidation-${WrappedComponent.displayName || 'WrappedFormElement'}`
  return ComponentWithFormValidation
}

const isFieldRequired = (fieldName: string, schema: ZodType): boolean => {
  if (!schema) return false

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const sourceSchema = 'sourceType' in schema ? (schema as any).sourceType() : schema
  const shape = 'shape' in sourceSchema ? (sourceSchema as { shape: Record<string, ZodType> }).shape : null

  if (!shape) return false

  const fieldSchema = shape[fieldName.split('.')[0] as string]
  if (!fieldSchema) return false

  return !('isOptional' in fieldSchema && (fieldSchema as { isOptional: () => boolean }).isOptional()) &&
         !('isNullable' in fieldSchema && (fieldSchema as { isNullable: () => boolean }).isNullable())
}
